import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import Text from '../../components/text';
import Ionicons from '@expo/vector-icons/Ionicons';
import Urls from '../../services/Urls';
import ajax from '../../helpers/ajaxHelper';
import useProperties from '../../hooks/usePropertiesApi';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import DropDownPicker from '../../components/picker/DropDownPicker';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import DatePickerComponent from '../../components/datepicker/DatePickerComponent';
import PopUp from '../../components/popUp/PopupView';
import { WSnackBar } from './../../vendor/react-native-smart-tip';
import { useSelector } from 'react-redux';
import { useRoute, useNavigation } from '@react-navigation/native';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const AddReminder = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const userDetails = useSelector((state) => state?.Login?.userDetails);
  const responseProperties = useProperties(true);

  const [mode, setMode] = useState('ADD');
  const [isVisible, setIsVisible] = useState(false);
  const [reminderTypes, setReminderTypes] = useState([]);

  const [state, setState] = useState({
    title: '',
    description: '',
    date: null,
    remainder_type: '',
    property_id: '',
    added_by: '',
  });

  useEffect(() => {
    if (userDetails?.first_name) {
      const fullName = `${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim();
      setState((prev) => ({ ...prev, added_by: fullName }));
    }
  }, [userDetails?.first_name, userDetails?.last_name]);

  useEffect(() => {
    if (route?.params?.id) {
      setMode('VIEW');
      _getDetails(route?.params?.id);
    }

    ajax
      .get(Urls.GET_REM_TYPES)
      .then(({ data }) => {
        if (data?.status && data?.data?.data) {
          const temp = data.data.data.map((element) => ({
            label: element.name,
            value: element.id,
          }));
          setReminderTypes(temp);
        }
      })
      .catch((error) => console.log('error rem types', error));
  }, []);

  const _handleChange = (e) => {
    setState((prev) => ({ ...prev, [e.label]: e.value }));
  };

  const _onDateChange = (e) => {
    const dateVal = e?.value === 'Invalid date' ? null : e?.value;
    setState((prev) => ({ ...prev, [e.label]: dateVal }));
  };

  const _getDetails = (id) => {
    ajax
      .get(`${Urls.ADD_REMAINDER}/${id}?type=${route.params?.type || 'other'}`)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setState((prev) => ({
            ...prev,
            title: data?.payload?.title || '',
            description: data?.payload?.description || '',
            date: data?.payload?.date || null,
            remainder_type: data?.payload?.type?.id || '',
            property_id: data?.payload?.property?.id?.toString() || '',
            added_by: data?.payload?.added_by || '',
          }));
        }
      })
      .catch((error) => console.log('error get details', error));
  };

  const _onSave = () => {
    if (!state.date) {
      WSnackBar.show({
        data: 'Kindly add reminder date.',
        position: WSnackBar.position.TOP,
        duration: WSnackBar.duration.LONG,
        textColor: 'white',
        backgroundColor: 'red',
      });
      return;
    }

    if (!state.remainder_type || !state.property_id) {
      WSnackBar.show({
        data: 'Please complete all required fields.',
        position: WSnackBar.position.TOP,
        duration: WSnackBar.duration.LONG,
        textColor: 'white',
        backgroundColor: 'red',
      });
      return;
    }

    const payloadToSend = {
      ...state,
      title: state.title || 'Gas Certificate',
    };

    ajax
      .post(Urls.ADD_REMAINDER, payloadToSend)
      .then(({ data }) => {
        if (data.status) {
          navigation.navigate('Reminder');
        }
      })
      .catch((error) => console.log('error save reminder', error));
  };

  const _onUpdate = () => {
    if (!state.date) {
      WSnackBar.show({
        data: 'Kindly add reminder date.',
        position: WSnackBar.position.TOP,
        duration: WSnackBar.duration.LONG,
        textColor: 'white',
        backgroundColor: 'red',
      });
      return;
    }

    ajax
      .put(`${Urls.ADD_REMAINDER}/${route?.params?.id}`, state)
      .then(({ data }) => {
        if (data.status) {
          navigation.navigate('Reminder');
        }
      })
      .catch((error) => console.log('error update reminder', error));
  };

  const _onDelete = () => {
    ajax
      .delete(`${Urls.ADD_REMAINDER}/${route?.params?.id}`)
      .then(({ data }) => {
        if (data.status) {
          setIsVisible(false);
          navigation.navigate('Reminder');
        }
      })
      .catch((error) => console.log('error delete reminder', error));
  };

  const isEditable = route?.params?.type === 'other' || mode === 'ADD';

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={false}>
      {/* Breadcrumb / Top Navigation */}
      <Text style={styles.breadcrumbText}>Back / Reminders</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>
          {mode === 'ADD' ? 'Add New Reminder' : 'Reminder Information'}
        </Text>

        {mode !== 'ADD' && (
          <TouchableOpacity onPress={() => setIsVisible(true)} activeOpacity={0.8}>
            <Text style={styles.deleteHeaderLink}>🗑 Remove</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Main White Card Container matching Figma */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        <Text style={styles.sectionTitle}>Reminder Info</Text>

        {/* Title / Reminder Name */}
        <View style={styles.fieldGroup}>
          <TextInputComponent
            title="Reminder Title*"
            placeholder="e.g. Gas Certificate"
            value={state?.title}
            onChangeText={(val) => _handleChange({ value: val, label: 'title' })}
            height={44}
            disabled={!isEditable}
            error={state?.title ? false : '*Required'}
          />
        </View>

        {/* Type + Remind By Row */}
        <View style={[styles.rowGrid, isMobile && styles.rowGridMobile]}>
          <View style={[styles.colGrid, isMobile && styles.colGridMobile]}>
            <DropDownPicker
              items={reminderTypes}
              title="Type*"
              placeholder="Select Type"
              defaultValue={state?.remainder_type}
              disabled={!isEditable}
              onChangeItem={(e) => _handleChange({ value: e?.value, label: 'remainder_type' })}
              error={state?.remainder_type ? false : '*Required'}
            />
          </View>

          <View style={[styles.colGrid, isMobile && styles.colGridMobile]}>
            <DatePickerComponent
              value={state?.date}
              title="Remind By*"
              placeholder="DD/MM/YYYY"
              onUpdateInput={(date) => _onDateChange({ value: date, label: 'date' })}
              isDisable={!isEditable}
              error={state?.date ? false : '*Required'}
              minDate={new Date()}
            />
          </View>
        </View>

        {/* Property + Added By Row */}
        <View style={[styles.rowGrid, isMobile && styles.rowGridMobile]}>
          <View style={[styles.colGrid, isMobile && styles.colGridMobile]}>
            <DropDownPicker
              items={responseProperties}
              title="Property*"
              placeholder="Select Property"
              defaultValue={state?.property_id}
              disabled={!isEditable}
              onChangeItem={(e) => _handleChange({ value: e?.value, label: 'property_id' })}
              error={state?.property_id ? false : '*Required'}
            />
          </View>

          <View style={[styles.colGrid, isMobile && styles.colGridMobile]}>
            <TextInputComponent
              title="Added By"
              value={state?.added_by}
              height={44}
              disabled={true}
            />
          </View>
        </View>

        {/* Notes Textarea */}
        <View style={styles.fieldGroup}>
          <TextInputComponent
            title="Notes*"
            placeholder="Start typing your notes here..."
            value={state?.description}
            onChangeText={(val) => _handleChange({ value: val, label: 'description' })}
            multiline={true}
            height={100}
            disabled={!isEditable}
          />
        </View>

        {/* Footer Action Buttons matching Figma */}
        <View style={styles.footerRow}>
          {mode === 'ADD' ? (
            <TouchableOpacity style={styles.addReminderBtn} onPress={_onSave} activeOpacity={0.85}>
              <Text style={styles.addReminderBtnText}>Add Reminder</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={styles.addReminderBtn} onPress={_onUpdate} activeOpacity={0.85}>
              <Text style={styles.addReminderBtnText}>Save Changes</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={styles.cancelBtn}
            onPress={() => navigation.navigate('Reminder')}
            activeOpacity={0.8}
          >
            <Text style={styles.cancelBtnText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Delete Popup */}
      <PopUp
        modalVisible={isVisible}
        titleText="Delete Reminder?"
        titleColor="#6C2BD9"
        subTitleText="Are you sure you want to delete this reminder?"
        subTitleColor="black"
        actionButtons={[
          {
            text: 'Delete',
            width: 100,
            onPress: _onDelete,
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setIsVisible(false),
          },
        ]}
        closePopup={() => setIsVisible(false)}
      />
    </ScrollView>
  );
};

export default drawerHoc({
  Component: AddReminder,
  title: 'Add Reminder',
  showBackButton: true,
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },

  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  deleteHeaderLink: {
    fontSize: 13,
    fontWeight: '600',
    color: '#EF4444',
  },

  /* MAIN WHITE CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 24,
    marginBottom: 40,
    ...SHADOWS.sm,
  },
  cardContainerMobile: {
    padding: 16,
    borderRadius: BORDER_RADIUS.md,
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 16,
  },

  fieldGroup: {
    marginBottom: 16,
  },

  rowGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  rowGridMobile: {
    flexDirection: 'column',
    gap: 12,
  },

  colGrid: {
    flex: 1,
  },
  colGridMobile: {
    width: '100%',
  },

  /* FOOTER BUTTONS */
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },

  addReminderBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 24,
    paddingVertical: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addReminderBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  cancelBtn: {
    backgroundColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 20,
    paddingVertical: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
});
